import React, { useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import TopUpRequestTable from '../../../components/tables/TopUpRequestTable';

const formatDate = (date) => date.toISOString().split('T')[0];

const parseDate = (value) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const distributorTopUpRequests = [
  {
    id: 'DTR0001',
    userName: 'Primary Float Account',
    date: '2025-10-30',
    amount: 250000,
    mode: 'NEFT',
    orderId: 'DTO881201',
    utr: 'DTOUTR44101',
    bank: 'HDFC Bank',
    transDate: '2025-10-29',
    remark: 'Monthly liquidity requirement',
    reason: '',
    parentId: { mobile: 'MD-9088776655' },
    userId: { mobile: 'DIS-9822334455' },
    status: 'Submitted',
    approvedBy: '-',
    approvedDate: '-',
    recorn: 'Pending',
    time: '08:55 AM',
  },
  {
    id: 'DTR0002',
    userName: 'Settlement Desk',
    date: '2025-10-28',
    amount: 175000,
    mode: 'RTGS',
    orderId: 'DTO881202',
    utr: 'DTOUTR44102',
    bank: 'Axis Bank',
    transDate: '2025-10-28',
    remark: 'High AEPS settlements',
    reason: '',
    parentId: { mobile: 'MD-9088776655' },
    userId: { mobile: 'DIS-9766554433' },
    status: 'Approved',
    approvedBy: 'Master Ops',
    approvedDate: '2025-10-28',
    recorn: 'Yes',
    time: '01:20 PM',
  },
  {
    id: 'DTR0003',
    userName: 'Retailer Support Float',
    date: '2025-10-26',
    amount: 98000,
    mode: 'UPI',
    orderId: 'DTO881203',
    utr: 'DTOUTR44103',
    bank: 'ICICI Bank',
    transDate: '2025-10-25',
    remark: 'DMT payouts buffer',
    reason: 'Proof submission pending',
    parentId: { mobile: 'MD-9088776655' },
    userId: { mobile: 'DIS-9644332211' },
    status: 'Pending',
    approvedBy: '-',
    approvedDate: '-',
    recorn: 'Follow-up',
    time: '05:05 PM',
  },
  {
    id: 'DTR0004',
    userName: 'Operations Float',
    date: '2025-10-24',
    amount: 210000,
    mode: 'IMPS',
    orderId: 'DTO881204',
    utr: 'DTOUTR44104',
    bank: 'State Bank of India',
    transDate: '2025-10-24',
    remark: 'Festival demand spike',
    reason: '',
    parentId: { mobile: 'MD-9088776655' },
    userId: { mobile: 'DIS-9556677889' },
    status: 'Submitted',
    approvedBy: '-',
    approvedDate: '-',
    recorn: 'In Review',
    time: '10:40 AM',
  },
  {
    id: 'DTR0005',
    userName: 'Retailer Float',
    date: '2025-10-21',
    amount: 84000,
    mode: 'Cash Deposit',
    orderId: 'DTO881205',
    utr: 'DTOUTR44105',
    bank: 'Kotak Mahindra Bank',
    transDate: '2025-10-20',
    remark: 'Counter cash consolidation',
    reason: '',
    parentId: { mobile: 'MD-9088776655' },
    userId: { mobile: 'DIS-9887766554' },
    status: 'Approved',
    approvedBy: 'Master Ops',
    approvedDate: '2025-10-21',
    recorn: 'Yes',
    time: '03:30 PM',
  },
  {
    id: 'DTR0006',
    userName: 'Support Float',
    date: '2025-10-18',
    amount: 62000,
    mode: 'NEFT',
    orderId: 'DTO881206',
    utr: 'DTOUTR44106',
    bank: 'Union Bank of India',
    transDate: '2025-10-18',
    remark: 'Weekly replenishment',
    reason: '',
    parentId: { mobile: 'MD-9088776655' },
    userId: { mobile: 'DIS-9332211445' },
    status: 'Pending',
    approvedBy: '-',
    approvedDate: '-',
    recorn: 'Pending',
    time: '11:55 AM',
  },
];

const identifyDateRange = (records = []) => records.reduce(
  (range, record) => {
    const requestDate = parseDate(record?.date) || parseDate(record?.transDate);

    if (!requestDate) {
      return range;
    }

    const next = { ...range };

    if (!next.min || requestDate < next.min) {
      next.min = requestDate;
    }

    if (!next.max || requestDate > next.max) {
      next.max = requestDate;
    }

    return next;
  },
  { min: null, max: null }
);

const createDefaultFilters = (records) => {
  const range = identifyDateRange(records);
  const today = formatDate(new Date());

  return {
    fromDate: range.min ? formatDate(range.min) : today,
    toDate: range.max ? formatDate(range.max) : today,
    searchValue: '',
    pageNumber: 0,
    pageSize: 10,
  };
};

const SendTopUpRequest = () => {
  const defaultFilters = useMemo(() => createDefaultFilters(distributorTopUpRequests), []);

  const [filters, setFilters] = useState(defaultFilters);
  const [allRequests, setAllRequests] = useState(distributorTopUpRequests);
  const [requestsData, setRequestsData] = useState([]);
  const [meta, setMeta] = useState({
    totalRecords: 0,
    totalPages: 0,
    currentPage: 0,
    pageSize: defaultFilters.pageSize,
  });

  useEffect(() => {
    const from = parseDate(filters.fromDate);
    const to = parseDate(filters.toDate);
    const normalizedSearch = filters.searchValue?.trim().toLowerCase() ?? '';

    const filterByDate = (request) => {
      const requestDate = parseDate(request?.date) || parseDate(request?.transDate);

      if (!from && !to) {
        return true;
      }

      if (!requestDate) {
        return false;
      }

      if (from && requestDate < from) {
        return false;
      }

      if (to) {
        const normalizedTo = new Date(to);
        normalizedTo.setHours(23, 59, 59, 999);
        if (requestDate > normalizedTo) {
          return false;
        }
      }

      return true;
    };

    const includesSearchValue = (request) => {
      if (!normalizedSearch) {
        return true;
      }

      const searchInValue = (value) => {
        if (value === null || value === undefined) {
          return false;
        }

        if (typeof value === 'object') {
          return Object.values(value).some((nested) => searchInValue(nested));
        }

        return String(value).toLowerCase().includes(normalizedSearch);
      };

      return Object.values(request).some(searchInValue);
    };

    const filtered = allRequests.filter((request) => filterByDate(request) && includesSearchValue(request));

    const totalRecords = filtered.length;
    const totalPages = totalRecords > 0 ? Math.ceil(totalRecords / filters.pageSize) : 0;
    const lastPageIndex = Math.max(totalPages - 1, 0);
    const currentPage = Math.min(Math.max(filters.pageNumber, 0), lastPageIndex);
    const startIndex = currentPage * filters.pageSize;
    const pageSlice = filtered.slice(startIndex, startIndex + filters.pageSize);

    setRequestsData(pageSlice);
    setMeta({
      totalRecords,
      totalPages,
      currentPage,
      pageSize: filters.pageSize,
    });

    if (currentPage !== filters.pageNumber) {
      setFilters((prev) => ({ ...prev, pageNumber: currentPage }));
    }
  }, [allRequests, filters]);

  const handleDateChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      pageNumber: 0,
    }));
  };

  const handleSearch = (value) => {
    setFilters((prev) => ({
      ...prev,
      searchValue: value,
      pageNumber: 0,
    }));
  };

  const handleTableDataChange = (updatedPageData = []) => {
    if (!Array.isArray(updatedPageData)) {
      return;
    }

    setAllRequests((prev) => {
      const next = [...prev];
      const indexById = new Map(next.map((item, index) => [item.id, index]));

      updatedPageData.forEach((item) => {
        if (!item || !item.id) {
          return;
        }

        if (indexById.has(item.id)) {
          const idx = indexById.get(item.id);
          next[idx] = { ...next[idx], ...item };
        } else {
          next.unshift(item);
        }
      });

      return next;
    });
  };

  const handlePageChange = (pageNumber) => {
    setFilters((prev) => ({
      ...prev,
      pageNumber,
    }));
  };

  const handlePageSizeChange = (pageSize) => {
    setFilters((prev) => ({
      ...prev,
      pageSize,
      pageNumber: 0,
    }));
  };

  return (
    <Container fluid>
      <TopUpRequestTable
        data={requestsData}
        loading={false}
        pagination={{
          totalRecords: meta.totalRecords,
          pageSize: filters.pageSize,
          currentPage: meta.currentPage,
          totalPages: meta.totalPages,
        }}
        onDataChange={handleTableDataChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        searchValue={filters.searchValue}
        onSearch={handleSearch}
        fromDate={filters.fromDate}
        toDate={filters.toDate}
        onDateChange={handleDateChange}
        showActions={false}
      />
    </Container>
  );
};

export default SendTopUpRequest;

